/**
 * Dev-only UI personas + custom flat role lists for simulating permissions.
 * Does not change API authorization.
 */

export type RoleRow = { name: string; can: string[] };

export const ROLE_VIEW_STORAGE_KEY = "promang_role_view_override_v1";

export type RoleViewPresetId =
  | "off"
  | "none"
  | "hours_time_only"
  | "hours_extended"
  | "service_field"
  | "projects_participant"
  | "project_leader_all"
  | "project_leader_selected";

export const ROLE_VIEW_PRESET_ORDER: RoleViewPresetId[] = [
  "off",
  "none",
  "hours_time_only",
  "hours_extended",
  "service_field",
  "projects_participant",
  "project_leader_all",
  "project_leader_selected",
];

export type RoleViewOverrideKind = "off" | "preset" | "custom";

export type RoleViewStoredState =
  | { v: 2; kind: "off" }
  | { v: 2; kind: "preset"; preset: RoleViewPresetId }
  | {
      v: 2;
      kind: "custom";
      flatNames: string[];
      /** Per-project full-access grants (ProjectAccess.admin) for custom simulation. */
      projectAccessIds?: Array<string | number>;
    };

/** Synthetic roles for each preset (no admin role — admin bypass is disabled while active). */
export function syntheticRolesForPreset(preset: RoleViewPresetId): RoleRow[] | null {
  if (preset === "off") return null;
  if (preset === "none") return [];

  if (preset === "hours_time_only") {
    return [
      {
        name: "hours",
        can: ["read", "create", "edit", "update", "delete"],
      },
    ];
  }

  if (preset === "hours_extended") {
    return [
      { name: "hours", can: ["*"] },
      { name: "hours_approval", can: ["read"] },
      { name: "hour_settings", can: ["read"] },
      { name: "hours_register", can: ["read", "update"] },
    ];
  }

  if (preset === "service_field") {
    return [
      { name: "service", can: ["*"] },
      { name: "service_reports", can: ["*"] },
      { name: "service_agreements", can: ["read"] },
    ];
  }

  if (preset === "projects_participant") {
    return [
      {
        name: "projects",
        can: [
          "read",
          "view",
          "add_notes",
          "time_register",
          "create_work_orders",
          "create_reports",
        ],
      },
      { name: "projects_work-orders", can: ["read", "view", "execute", "time_register"] },
    ];
  }

  // Project leader with company-wide project admin (all projects). Mirrors the projects_admin role.
  if (preset === "project_leader_all") {
    return [
      { name: "projects_admin", can: ["read", "edit", "delete", "view"] },
      { name: "projects", can: ["read", "view", "edit"] },
      { name: "projects_edit", can: ["read", "edit"] },
      { name: "projects_create", can: ["read", "create"] },
      { name: "projects_plan", can: ["read"] },
      { name: "projects_work-orders", can: ["read", "view", "edit"] },
      { name: "projects_work-orders_edit", can: ["read", "edit"] },
    ];
  }

  // Project leader scoped to selected projects (full access granted per-project via ProjectAccess.admin).
  if (preset === "project_leader_selected") {
    return [
      { name: "projects", can: ["read", "view", "edit"] },
      { name: "projects_edit", can: ["read", "edit"] },
      { name: "projects_create", can: ["read", "create"] },
      { name: "projects_plan", can: ["read"] },
      { name: "projects_work-orders", can: ["read", "view", "edit"] },
      { name: "projects_work-orders_edit", can: ["read", "edit"] },
    ];
  }

  return [];
}

function migrateLegacy(raw: unknown): RoleViewStoredState {
  if (
    raw &&
    typeof raw === "object" &&
    "v" in raw &&
    (raw as { v?: number }).v === 2
  ) {
    const o = raw as RoleViewStoredState;
    if (o.kind === "off") return { v: 2, kind: "off" };
    if (o.kind === "preset" && o.preset && ROLE_VIEW_PRESET_ORDER.includes(o.preset)) {
      if (o.preset === "off") return { v: 2, kind: "off" };
      return { v: 2, kind: "preset", preset: o.preset };
    }
    if (o.kind === "custom" && Array.isArray(o.flatNames)) {
      const projectAccessIds = Array.isArray(
        (o as { projectAccessIds?: unknown }).projectAccessIds
      )
        ? (o as { projectAccessIds: unknown[] }).projectAccessIds.filter(
            (x): x is string | number =>
              typeof x === "string" || typeof x === "number"
          )
        : undefined;
      return {
        v: 2,
        kind: "custom",
        flatNames: o.flatNames.filter((x): x is string => typeof x === "string"),
        ...(projectAccessIds?.length ? { projectAccessIds } : {}),
      };
    }
  }
  const legacy = raw as { preset?: RoleViewPresetId } | null;
  if (
    legacy?.preset != null &&
    ROLE_VIEW_PRESET_ORDER.includes(legacy.preset)
  ) {
    if (legacy.preset === "off") return { v: 2, kind: "off" };
    return { v: 2, kind: "preset", preset: legacy.preset };
  }
  return { v: 2, kind: "off" };
}

export function readStoredRoleViewState(): RoleViewStoredState {
  if (typeof window === "undefined") return { v: 2, kind: "off" };
  try {
    const raw = window.localStorage.getItem(ROLE_VIEW_STORAGE_KEY);
    if (!raw) return { v: 2, kind: "off" };
    return migrateLegacy(JSON.parse(raw));
  } catch {
    return { v: 2, kind: "off" };
  }
}

export function writeStoredRoleViewState(state: RoleViewStoredState): void {
  if (typeof window === "undefined") return;
  try {
    if (state.kind === "off") {
      window.localStorage.removeItem(ROLE_VIEW_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(ROLE_VIEW_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}
